import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconSearch,
  IconChevronUp,
  IconChevronDown,
  IconPencil,
  IconTrash,
  IconX,
} from "@tabler/icons-react";

import { useDispatch, useSelector } from "react-redux";
import {
  getListContactUsMessages,
  deleteContactUsMessage,
  searchContactUsMessages
} from "../../redux/slices/contactUsSlice";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

/* =========================================
   1. UPDATED Delete Confirmation Modal (Now handles both single and bulk)
========================================= */
function DeleteMessageModal({ items, onClose, onConfirm }) {
    const isBulkDelete = Array.isArray(items);
    let messageText;

    if (isBulkDelete) {
        messageText = `Are you sure you want to delete these ${items.length} selected messages? This action cannot be undone.`;
    } else {
        messageText = `Are you sure you want to delete the message from <strong>${items.full_name}</strong>? This action cannot be undone.`;
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                >
                    <IconX size={20} />
                </button>
                <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                    <IconTrash size={20} />
                    Delete Message(s)
                </h2>
                <p className="text-sm text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: messageText }} />
                <div className="flex justify-end gap-4 mt-6">
                     <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}


/* =========================================
   Main Component: ContactMessages
========================================= */
function ContactMessages() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const { loading, error, messages } = useSelector((state) => state.contactUs);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [itemsToDelete, setItemsToDelete] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    setLayoutConfig({
      title: "Contact Us Messages",
      showBackButton: false,
      scrollable: false,
    });
    dispatch(getListContactUsMessages());
  }, [setLayoutConfig, dispatch]);

  useEffect(() => {
    if (searchTerm.trim() !== '') {
      const timer = setTimeout(() => {
        dispatch(searchContactUsMessages(searchTerm));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      dispatch(getListContactUsMessages());
    }
  }, [searchTerm, dispatch]);

  const filteredMessages = messages || [];
  const totalCount = filteredMessages.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentMessages = filteredMessages.slice(startIndex, endIndex);
  const fromCount = startIndex + 1;
  const toCount = endIndex > totalCount ? totalCount : endIndex;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };


  const handleDeleteMessage = (message) => {
    setItemsToDelete(message);
    setDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedMessages.length > 0) {
      setItemsToDelete(selectedMessages);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = () => {
    const isBulkDelete = Array.isArray(itemsToDelete);

    if (isBulkDelete) {
      // Your original bulk delete logic
      itemsToDelete.forEach(id => {
        dispatch(deleteContactUsMessage(id));
      });
      setSelectedMessages([]);
    } else {
      // Your original single delete logic
      dispatch(deleteContactUsMessage(itemsToDelete.id));
    }
    
    setDeleteModalOpen(false);
    setItemsToDelete(null);
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev =>
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const selectAllMessages = () => {
    if (selectedMessages.length === currentMessages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(currentMessages.map(message => message.id));
    }
  };

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(getListContactUsMessages())} />;
  }

  return (
    <div>
      <main className="flex-1 bg-[#FFFFFF] text-gray-800 px-4 rounded">
        <div className="rounded-md bg-[#FFFFFF] py-4">
          {/* TOOLBAR */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {selectedMessages.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {selectedMessages.length} selected
                  </span>

                  <div className="relative ml-1">
                    <input
                      type="text"
                      placeholder="Search By Name, Email or Subject"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                                 focus:outline-none focus:border-[3px] peer"
                    />
                    <IconSearch
                      size={16}
                      className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                    onClick={handleBulkDelete}
                  >
                    <IconTrash size={16} />
                    Delete
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                    onClick={() => setSelectedMessages([])}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="relative ml-1">
                  <input
                    type="text"
                    placeholder="Search By Name, Email or Subject"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                                  focus:outline-none focus:border-[3px] peer"
                  />
                  <IconSearch
                    size={16}
                    className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                  />
                </div>
                <span className="text-sm text-gray-700 ml-auto mr-1">
                  {fromCount} - {toCount} of {totalCount}
                </span>
                <button onClick={handlePrevPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronUp size={18} />
                </button>
                <button onClick={handleNextPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronDown size={18} />
                </button>
              </>
            )}
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto h-[calc(100vh-280px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                <tr className="bg-[#F9F9FF] text-[#120E43]">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedMessages.length > 0 && selectedMessages.length === currentMessages.length}
                      onChange={selectAllMessages}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Picture</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Message</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Is Processed</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentMessages.map((message) => (
                  <tr
                    key={message.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedMessages.includes(message.id)}
                        onChange={() => toggleMessageSelection(message.id)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <img
                        src={message.profile_picture}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    </td>
                    <td className="px-4 py-2">{message.full_name}</td>
                    <td className="px-4 py-2">{message.email}</td>
                    <td className="px-4 py-2 max-w-xs truncate">{message.message}</td>
                    <td className="px-4 py-2">{new Date(message.submitted_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}</td>
                    <td className="px-4 py-2">
                      {message.is_processed ? (
                        <span className="inline-block w-4 h-4 bg-green-500 rounded-full"></span>
                      ) : (
                        <span className="inline-block w-4 h-4 bg-red-500 rounded-full"></span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={() => navigate(`/admin/contact-us-messages/${message.id}`)}
                        >
                          <IconPencil size={16} />
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 flex items-center gap-1"
                          onClick={() => handleDeleteMessage(message)}
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {currentMessages.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">
                      {searchTerm ? "No matching messages found" : "No messages found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* BOTTOM SUMMARY ROW */}
          <div className="mt-4 flex items-center justify-between bg-white border-t border-gray-200 p-4 rounded-md shadow-sm">
            <div className="flex items-center gap-4 text-sm text-gray-700">
              <span className="flex items-center gap-1">
                <span role="img" aria-label="messages">📩</span>
                Total Messages: <strong>{messages.length}</strong>
              </span>
              <span className="flex items-center gap-1 text-blue-600">
                <span role="img" aria-label="new">🆕</span>
                New Today: <strong>{messages.filter(m => new Date(m.submitted_at).toDateString() === new Date().toDateString()).length}</strong>
              </span>
            </div>
          </div>
        </div>
      </main>

      {/* 4. UPDATED MODAL INVOCATION */}
      {deleteModalOpen && itemsToDelete && (
        <DeleteMessageModal
          items={itemsToDelete}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

export default ContactMessages;